import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pause, Play, Plus, XCircle } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useSearch } from 'wouter';
import { EmptyState } from '../components/ui/EmptyState';
import { SearchInput } from '../components/ui/SearchInput';
import { Select } from '../components/ui/Select';
import { StaffPicker } from '../components/ui/StaffPicker';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useStaffList } from '../lib/options';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { useDebounced } from '../lib/useDebounced';
import { Loader } from '../components/ui/Spinner';

type Loan = { id: string; userId: string; type: 'loan' | 'advance'; principalAmount: string; monthlyInstalment: string | null; balance: string; startMonth: string; status: 'active' | 'closed' | 'paused'; note: string | null };

const STATUS_STYLE: Record<string, string> = { active: 'bg-brand-100 text-brand-700', paused: 'bg-amber-100 text-amber-700', closed: 'bg-slate-100 text-slate-600' };
const money = (v: string) => `₹${Number(v).toLocaleString('en-IN')}`;

function NewLoanForm({ defaultUserId, onDone }: { defaultUserId: string; onDone: () => void }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const [userId, setUserId] = useState(defaultUserId);
  const [type, setType] = useState<'loan' | 'advance'>('advance');
  const [principalAmount, setPrincipalAmount] = useState('');
  const [monthlyInstalment, setMonthlyInstalment] = useState('');
  const [startMonth, setStartMonth] = useState(new Intl.DateTimeFormat('en-CA').format(new Date()).slice(0, 8) + '01');
  const [note, setNote] = useState('');

  const create = useMutation({
    mutationFn: () => api.post('/api/loans', { userId, type, principalAmount, monthlyInstalment: type === 'loan' ? monthlyInstalment : undefined, startMonth, note: note || undefined }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['loans'] }); success(type === 'loan' ? 'Loan recorded' : 'Advance recorded'); onDone(); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not save')),
  });

  return (
    <form className="card mb-6 grid gap-4 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
      <label className="label">Staff member<StaffPicker required value={userId} onChange={setUserId} /></label>
      <label className="label">Type<Select className="mt-1" value={type} onChange={(e) => setType(e.target.value as 'loan' | 'advance')}><option value="advance">Advance (recovered next salary)</option><option value="loan">Loan (recovered in instalments)</option></Select></label>
      <label className="label">Amount (₹)<input type="number" min="1" className="field mt-1" required value={principalAmount} onChange={(e) => setPrincipalAmount(e.target.value)} /></label>
      {type === 'loan' && <label className="label">Monthly instalment (₹)<input type="number" min="1" className="field mt-1" required value={monthlyInstalment} onChange={(e) => setMonthlyInstalment(e.target.value)} /></label>}
      <label className="label">Start month<input type="date" className="field mt-1" required value={startMonth} onChange={(e) => setStartMonth(e.target.value)} /></label>
      <label className="label sm:col-span-2">Note<input className="field mt-1" value={note} onChange={(e) => setNote(e.target.value)} /></label>
      <div className="flex items-end gap-2 sm:col-span-2">
        <button className="btn-primary" disabled={create.isPending}>Save</button>
        <button type="button" className="btn-secondary" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

function LoanActions({ loan }: { loan: Loan }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const setStatus = useMutation({
    mutationFn: ({ status, note }: { status: 'closed' | 'paused' | 'active'; note?: string }) => api.patch(`/api/loans/${loan.id}`, { status, ...(note ? { note } : {}) }),
    onSuccess: (_d, v) => { void client.invalidateQueries({ queryKey: ['loans'] }); success(`Loan ${v.status}`); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not update loan')),
  });
  function closeOrPause(status: 'closed' | 'paused') {
    const note = window.prompt(`Reason to mark this ${loan.type} as ${status}?`, loan.note ?? '');
    if (note === null) return;
    if (!note.trim()) { toastError('A reason is required'); return; }
    setStatus.mutate({ status, note });
  }
  if (loan.status === 'closed') return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {loan.status === 'active' && <button className="btn-secondary" disabled={setStatus.isPending} onClick={() => closeOrPause('paused')}><Pause size={16} /> Pause</button>}
      {loan.status === 'paused' && <button className="btn-secondary" disabled={setStatus.isPending} onClick={() => setStatus.mutate({ status: 'active' })}><Play size={16} /> Resume</button>}
      <button className="btn-secondary" disabled={setStatus.isPending} onClick={() => closeOrPause('closed')}><XCircle size={16} /> Close</button>
    </div>
  );
}

export function LoansPage() {
  const { user } = useAuth();
  const search = new URLSearchParams(useSearch());
  const isManager = user?.roles.some((r) => r === 'hr' || r === 'admin') ?? false;
  const filterUserId = search.get('userId') ?? '';
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q);
  const [status, setStatus] = useState('');
  const staff = useStaffList();
  const loans = useQuery({
    queryKey: ['loans', filterUserId, { q: debouncedQ, status }],
    queryFn: async () => (await api.get<Loan[]>('/api/loans', { params: filterUserId ? { userId: filterUserId } : { q: debouncedQ || undefined, status: status || undefined } })).data,
  });
  const staffName = (id: string) => staff.data?.find((s) => s.id === id)?.name ?? id;
  const rows = loans.data ?? [];
  const showAllStaffFilters = isManager && !filterUserId;

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div><h1 className="text-3xl font-bold">Loans &amp; advances</h1><p className="mt-1 text-muted">{filterUserId ? staffName(filterUserId) : isManager ? 'All staff' : 'Your balance'}</p></div>
        {isManager && <button className="btn-primary" onClick={() => setOpen(!open)}><Plus size={18} /> New</button>}
      </div>

      {open && <NewLoanForm defaultUserId={filterUserId} onDone={() => setOpen(false)} />}

      {showAllStaffFilters && (
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <SearchInput className="sm:col-span-2" placeholder="Search by staff name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Select value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All statuses</option><option value="active">Active</option><option value="paused">Paused</option><option value="closed">Closed</option></Select>
        </div>
      )}

      {loans.isLoading && <Loader />}
      {!loans.isLoading && rows.length === 0 && <EmptyState title="No loans or advances" />}
      <div className="space-y-3">
        {rows.map((loan) => (
          <article key={loan.id} className="card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {isManager && !filterUserId && <strong className="block">{staffName(loan.userId)}</strong>}
                <p className="font-semibold capitalize">{loan.type} • {money(loan.principalAmount)}</p>
                <p className="mt-1 text-sm text-muted">Balance {money(loan.balance)}{loan.monthlyInstalment ? ` • ${money(loan.monthlyInstalment)}/month` : ''} • since {loan.startMonth}</p>
                {loan.note && <p className="mt-1 text-sm italic text-muted">"{loan.note}"</p>}
              </div>
              <span className={`badge shrink-0 ${STATUS_STYLE[loan.status]}`}>{loan.status}</span>
            </div>
            {isManager && <LoanActions loan={loan} />}
          </article>
        ))}
      </div>
    </>
  );
}
